import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/ui/page-header';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { optPage, optEnum } from '@/lib/list-search';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, RotateCcw, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { usePermissions } from '@/common/hooks/usePermissions';
import { requestsService, AssetRequest } from '@/features/requests/services/requests.service';
import { RequestDetailsDialog } from '@/features/requests/components/RequestDetailsDialog';
import { ReturnRequestDialog } from '@/features/requests/components/ReturnRequestDialog';
import { formatPlannedReturn, isRequestOverdue } from '@/features/requests/lib/overdue';
import { Pagination } from '@/components/ui/pagination';
import { ViewModeToggle, type ViewMode } from '@/components/ui/view-mode-toggle';

export interface RequestsSearch {
  status?: 'pending' | 'approved' | 'rejected' | 'returned';
  type?: 'withdrawal' | 'return';
  page?: number;
}

export const validateRequestsSearch = (s: Record<string, unknown>): RequestsSearch => ({
  status: optEnum(s.status, ['pending', 'approved', 'rejected', 'returned'] as const),
  type: optEnum(s.type, ['withdrawal', 'return'] as const),
  page: optPage(s.page),
});

export const Route = createFileRoute('/_authenticated/requests')({
  validateSearch: validateRequestsSearch,
  component: RequestsPage,
});

function RequestsPage() {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { status: statusFilter, type: typeFilter, page = 1 } = Route.useSearch();
  const navigate = Route.useNavigate();
  // mudar filtro volta para a página 1
  const setParams = (patch: Partial<RequestsSearch>) =>
    navigate({ search: (prev) => ({ ...prev, page: undefined, ...patch }), replace: true });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AssetRequest | null>(null);
  const [returnTarget, setReturnTarget] = useState<AssetRequest | null>(null);
  const [limit] = useState(10);
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  const canApprove = hasPermission('requests.approve');

  // Buscar solicitações
  const { data: requestsData, isLoading } = useQuery({
    queryKey: ['requests', statusFilter, typeFilter, page, limit],
    queryFn: async () => {
      return requestsService.getAllPaginated({
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        page,
        limit,
      });
    },
  });
  const requests = requestsData?.items || [];
  const total = requestsData?.total || 0;

  // Mutations
  const approveMutation = useMutation({
    mutationFn: (id: string) => requestsService.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => requestsService.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
  });

  const handleApprove = async (request: AssetRequest) => {
    const ok = await confirm({
      title: 'Aprovar esta solicitação?',
      confirmLabel: 'Aprovar',
    });
    if (!ok) return;

    try {
      await approveMutation.mutateAsync(request.id);
    } catch (error) {
      // o interceptor global já mostra o toast de erro
      console.error('Erro ao aprovar:', error);
    }
  };

  const handleReject = async (request: AssetRequest) => {
    const ok = await confirm({
      title: 'Rejeitar esta solicitação?',
      confirmLabel: 'Rejeitar',
      destructive: true,
    });
    if (!ok) return;

    try {
      await rejectMutation.mutateAsync(request.id);
    } catch (error) {
      // o interceptor global já mostra o toast de erro
      console.error('Erro ao rejeitar:', error);
    }
  };

  const handleReturn = (request: AssetRequest) => setReturnTarget(request);

  const handleViewDetails = (request: AssetRequest) => {
    setSelectedRequest(request);
    setDetailsOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      pending: 'secondary',
      approved: 'default',
      rejected: 'destructive',
      returned: 'outline',
    };

    const labels: Record<string, string> = {
      pending: 'Pendente',
      approved: 'Aprovada',
      rejected: 'Rejeitada',
      returned: 'Devolvida',
    };

    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  const getTypeLabel = (type: string) => {
    return type === 'withdrawal' ? 'Retirada' : 'Devolução';
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Solicitações"
        description="Gerencie solicitações de retirada e devolução"
      />

      <Card>
        <CardHeader>
          <CardTitle>Lista de Solicitações</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filtros */}
          <div className="mb-4 flex flex-col gap-4 md:flex-row">
            <Select
              value={statusFilter}
              onValueChange={(value) =>
                setParams({ status: value === 'all' ? undefined : (value as RequestsSearch['status']) })
              }
            >
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="approved">Aprovada</SelectItem>
                <SelectItem value="rejected">Rejeitada</SelectItem>
                <SelectItem value="returned">Devolvida</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={typeFilter}
              onValueChange={(value) =>
                setParams({ type: value === 'all' ? undefined : (value as RequestsSearch['type']) })
              }
            >
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="withdrawal">Retirada</SelectItem>
                <SelectItem value="return">Devolução</SelectItem>
              </SelectContent>
            </Select>

            {(statusFilter || typeFilter) && (
              <Button
                variant="outline"
                onClick={() => setParams({ status: undefined, type: undefined })}
              >
                Limpar Filtros
              </Button>
            )}
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
          </div>

          {/* Tabela */}
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : !requests || requests.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Nenhuma solicitação encontrada
            </div>
          ) : (
            <>
            {viewMode === 'table' ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Equipamento</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data Solicitação</TableHead>
                    <TableHead>Data Retorno Prevista</TableHead>
                    <TableHead>Data Devolução</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">
                        {request.asset?.code
                          ? `${request.asset.code} - ${request.asset.name}`
                          : request.asset?.name || '-'}
                      </TableCell>
                      <TableCell>{request.user?.email || '-'}</TableCell>
                      <TableCell>{getTypeLabel(request.type)}</TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell>
                        {format(new Date(request.requestedAt), 'dd/MM/yyyy HH:mm', {
                          locale: ptBR,
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {formatPlannedReturn(request.plannedReturnDate)}
                          {isRequestOverdue(request) && <Badge variant="destructive">Atrasado</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {request.actualReturnDate
                          ? format(new Date(request.actualReturnDate), 'dd/MM/yyyy HH:mm', {
                              locale: ptBR,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewDetails(request)}
                            title="Ver detalhes"
                            aria-label="Ver detalhes da solicitação"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canApprove && request.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleApprove(request)}
                                title="Aprovar"
                            aria-label="Aprovar solicitação"
                                className="text-green-600 hover:text-green-700"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleReject(request)}
                                title="Rejeitar"
                            aria-label="Rejeitar solicitação"
                                className="text-red-600 hover:text-red-700"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {request.status === 'approved' && request.type === 'withdrawal' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleReturn(request)}
                              title="Registrar Devolução"
                            aria-label="Registrar devolução"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <RotateCcw className="h-4 w-4" />
                              
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {requests.map((request) => (
                  <Card key={request.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">
                        {request.asset?.code
                          ? `${request.asset.code} - ${request.asset.name}`
                          : request.asset?.name || '-'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1 text-sm">
                      <p><strong>Usuário:</strong> {request.user?.email || '-'}</p>
                      <p><strong>Tipo:</strong> {getTypeLabel(request.type)}</p>
                      <p><strong>Status:</strong> {request.status}</p>
                      <p>
                        <strong>Solicitação:</strong>{' '}
                        {format(new Date(request.requestedAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </p>
                      <div className="pt-2 flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleViewDetails(request)}>
                          Ver detalhes
                        </Button>
                        {canApprove && request.status === 'pending' && (
                          <>
                            <Button size="sm" onClick={() => handleApprove(request)}>Aprovar</Button>
                            <Button variant="destructive" size="sm" onClick={() => handleReject(request)}>
                              Rejeitar
                            </Button>
                          </>
                        )}
                        {request.status === 'approved' && request.type === 'withdrawal' && (
                          <Button variant="outline" size="sm" onClick={() => handleReturn(request)}>
                            {request.batchId ? 'Devolver kit' : 'Devolver'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {total > limit && (
              <div className="mt-4">
                <Pagination total={total} page={page} limit={limit} onPageChange={(p) => setParams({ page: p > 1 ? p : undefined })} />
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalhes */}
      <RequestDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        request={selectedRequest}
        onApprove={handleApprove}
        onReject={handleReject}
        onReturn={handleReturn}
        canApprove={canApprove}
      />

      <ReturnRequestDialog
        open={!!returnTarget}
        onOpenChange={(open) => !open && setReturnTarget(null)}
        request={returnTarget}
      />
    </div>
  );
}
